import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Download, Search, FileText, ChevronDown, Rocket, History,
  ShieldCheck, AlertTriangle, ChevronRight, X,
} from 'lucide-react';
import { ADMIN_PALETTE, STATUS_PALETTE } from './adminDesignSystem';
import { AgreementRolloutModal } from './AgreementRolloutModal';
import { downloadOrShareBlob } from '../../utils/downloadOrShareBlob';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      timeZone: 'Europe/Athens',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

/** Force-download or share a blob so Capacitor Android + browsers all
 * hand the file off to the OS instead of silently swallowing it. Uses
 * the shared `downloadOrShareBlob` helper (native share sheet on mobile,
 * anchor download on desktop, blob-URL fallback). */
function triggerBlobDownload(blob, filename, mimeType) {
  downloadOrShareBlob(blob, filename, { mimeType });
}

const Tab = ({ active, onClick, label, count, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 14px',
      borderRadius: 12,
      border: `1px solid ${active ? ADMIN_PALETTE.ink : ADMIN_PALETTE.border}`,
      background: active ? ADMIN_PALETTE.ink : ADMIN_PALETTE.surface,
      color: active ? '#FFFFFF' : ADMIN_PALETTE.inkSoft,
      fontFamily: "'Manrope', sans-serif", fontSize: 13,
      fontWeight: active ? 700 : 600, cursor: 'pointer',
    }}
  >
    {label}
    {typeof count === 'number' ? (
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        minWidth: 20, height: 18, padding: '0 6px', borderRadius: 9999,
        background: active ? '#FFFFFF' : STATUS_PALETTE.info.soft,
        color: active ? ADMIN_PALETTE.ink : STATUS_PALETTE.info.text,
        fontSize: 10.5, fontWeight: 800,
      }}>
        {count > 99 ? '99+' : count}
      </span>
    ) : null}
  </button>
);

function VersionBanner({ stats, versions, onOpenRollout, onOpenHistory }) {
  if (!stats) return null;
  const pct = stats.signers_total ? Math.round((stats.signers_current / stats.signers_total) * 100) : 100;
  const anyRow = versions?.[0];
  return (
    <div
      data-testid="admin-agreements-version-banner"
      style={{
        display: 'flex', gap: 14, padding: 14, borderRadius: 16,
        background: `linear-gradient(135deg, ${ADMIN_PALETTE.surface} 0%, ${ADMIN_PALETTE.surfaceMuted} 100%)`,
        border: `1px solid ${ADMIN_PALETTE.border}`, alignItems: 'center', flexWrap: 'wrap',
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 44, height: 44, borderRadius: 12,
        background: 'rgba(120, 88, 214, 0.12)',
        color: 'var(--accent-purple, #7858D6)',
      }}>
        <ShieldCheck size={20} strokeWidth={2.2} />
      </div>
      <div style={{ flex: 1, minWidth: 200 }}>
        <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 13, fontWeight: 700, color: ADMIN_PALETTE.inkSoft, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          Current live version
        </p>
        <p style={{ margin: '2px 0 0', fontFamily: "'Inria Serif', serif", fontSize: 20, fontWeight: 800, color: ADMIN_PALETTE.ink }}>
          {stats.current_version}
          {anyRow?.changelog_note ? (
            <span style={{ marginLeft: 10, fontSize: 12.5, fontWeight: 600, color: ADMIN_PALETTE.inkMuted }}>
              · {anyRow.changelog_note}
            </span>
          ) : null}
        </p>
        <p style={{ margin: '2px 0 0', fontSize: 12, color: ADMIN_PALETTE.inkMuted }}>
          {stats.activated_at ? `Activated ${formatDate(stats.activated_at)}` : 'Seeded from code template'}
          {' · '}
          <strong style={{ color: ADMIN_PALETTE.ink }}>{stats.signers_current}/{stats.signers_total}</strong> signers on current ({pct}%)
        </p>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={onOpenHistory}
          data-testid="admin-agreements-open-history"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '9px 12px', borderRadius: 12,
            border: `1px solid ${ADMIN_PALETTE.border}`,
            background: ADMIN_PALETTE.surface, color: ADMIN_PALETTE.ink,
            fontWeight: 700, fontSize: 12.5, cursor: 'pointer',
          }}
        >
          <History size={13} />
          Version history
        </button>
        <button
          type="button"
          onClick={onOpenRollout}
          data-testid="admin-agreements-open-rollout"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '9px 12px', borderRadius: 12,
            border: 'none', background: ADMIN_PALETTE.ink, color: '#FFF',
            fontWeight: 800, fontSize: 12.5, cursor: 'pointer',
          }}
        >
          <Rocket size={13} />
          Roll out new version
        </button>
      </div>
    </div>
  );
}

function VersionHistoryDrawer({ open, onClose, kind, versions, headers, onLoadBody }) {
  const [expandedVersion, setExpandedVersion] = useState('');
  const [bodyCache, setBodyCache] = useState({});

  useEffect(() => { setExpandedVersion(''); }, [kind, open]);

  if (!open) return null;

  const toggle = async (version) => {
    if (expandedVersion === version) {
      setExpandedVersion('');
      return;
    }
    setExpandedVersion(version);
    if (bodyCache[version]) return;
    try {
      const body = await onLoadBody(version);
      setBodyCache((cur) => ({ ...cur, [version]: body || '' }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load version body');
    }
  };

  return (
    <div
      data-testid="admin-agreements-history-drawer"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(6px)', zIndex: 10001,
        display: 'flex', justifyContent: 'flex-end',
      }}
    >
      <div style={{
        width: 'min(560px, 100%)', height: '100%',
        background: ADMIN_PALETTE.surface, display: 'flex', flexDirection: 'column',
        boxShadow: '-20px 0 40px rgba(0,0,0,0.24)',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '14px 16px', borderBottom: `1px solid ${ADMIN_PALETTE.border}`,
        }}>
          <History size={16} color={ADMIN_PALETTE.ink} />
          <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 16, fontWeight: 800, flex: 1, color: ADMIN_PALETTE.ink }}>
            Version history — {kind === 'venue' ? 'Venue' : 'Host'}
          </p>
          <button
            type="button"
            onClick={onClose}
            data-testid="admin-agreements-history-close"
            aria-label="Close"
            style={{
              background: ADMIN_PALETTE.surfaceMuted, border: `1px solid ${ADMIN_PALETTE.border}`,
              borderRadius: 10, width: 32, height: 32, display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
              color: ADMIN_PALETTE.ink,
            }}
          >
            <X size={16} />
          </button>
        </div>
        <div style={{ padding: 12, overflowY: 'auto', flex: 1 }}>
          {versions.map((v) => (
            <div
              key={v.id || v.version}
              data-testid={`admin-agreements-history-row-${v.version}`}
              style={{
                padding: 12, marginBottom: 10, borderRadius: 12,
                background: ADMIN_PALETTE.surface,
                border: `1px solid ${v.active ? 'rgba(120, 88, 214, 0.55)' : ADMIN_PALETTE.border}`,
              }}
            >
              <div
                onClick={() => toggle(v.version)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
              >
                <ChevronRight
                  size={14}
                  color={ADMIN_PALETTE.inkMuted}
                  style={{
                    transform: expandedVersion === v.version ? 'rotate(90deg)' : 'rotate(0)',
                    transition: 'transform 180ms ease',
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 15, fontWeight: 800, color: ADMIN_PALETTE.ink }}>
                    {v.version}
                    {v.active ? (
                      <span style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 800, padding: '2px 8px', borderRadius: 999, background: 'rgba(120, 88, 214, 0.12)', color: 'var(--accent-purple, #7858D6)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                        Active
                      </span>
                    ) : null}
                  </p>
                  <p style={{ margin: '2px 0 0', fontSize: 11.5, color: ADMIN_PALETTE.inkMuted }}>
                    {v.activated_at ? formatDate(v.activated_at) : 'Seed'}
                    {v.changelog_note ? ` · ${v.changelog_note}` : ''}
                  </p>
                </div>
              </div>
              {expandedVersion === v.version ? (
                <pre style={{
                  margin: '10px 0 0', maxHeight: 300, overflowY: 'auto',
                  padding: 10, borderRadius: 10, background: ADMIN_PALETTE.surfaceMuted,
                  fontFamily: "'Inria Serif', serif", fontSize: 12, lineHeight: 1.5,
                  color: ADMIN_PALETTE.ink, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                }}>
                  {bodyCache[v.version] === undefined ? 'Loading…' : (bodyCache[v.version] || v.body || '(No body stored)')}
                </pre>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function AdminAgreementsPanel({ headers }) {
  const [kind, setKind] = useState('venue');
  const [items, setItems] = useState([]);
  const [regions, setRegions] = useState([]);
  const [search, setSearch] = useState('');
  const [region, setRegion] = useState('');
  const [onlyPending, setOnlyPending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [counts, setCounts] = useState({ venue: 0, host: 0 });
  const [versions, setVersions] = useState([]);
  const [stats, setStats] = useState(null);
  const [rolloutOpen, setRolloutOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/agreements`, {
        headers,
        params: {
          kind,
          region: region || undefined,
          q: search || undefined,
          only_pending: onlyPending || undefined,
        },
      });
      setItems(res.data?.items || []);
      setRegions(res.data?.regions || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load agreements');
    } finally {
      setLoading(false);
    }
  }, [headers, kind, region, search, onlyPending]);

  const fetchVersions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/agreement-versions`, { headers, params: { kind } });
      setVersions(res.data?.versions || []);
      setStats(res.data?.stats || null);
    } catch (_err) { /* silent */ }
  }, [headers, kind]);

  const fetchCounts = useCallback(async () => {
    try {
      const [venueRes, hostRes] = await Promise.all([
        axios.get(`${API}/admin/agreements`, { headers, params: { kind: 'venue' } }),
        axios.get(`${API}/admin/agreements`, { headers, params: { kind: 'host' } }),
      ]);
      setCounts({
        venue: (venueRes.data?.items || []).length,
        host: (hostRes.data?.items || []).length,
      });
    } catch (_err) { /* ignore */ }
  }, [headers]);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { fetchVersions(); }, [fetchVersions]);
  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput.trim()), 280);
    return () => clearTimeout(id);
  }, [searchInput]);

  const loadVersionBody = async (version) => {
    const res = await axios.get(`${API}/admin/agreement-versions/${encodeURIComponent(version)}/body`, {
      headers, params: { kind },
    });
    return res.data?.body || '';
  };

  const downloadOne = async (agreementId, label) => {
    try {
      const res = await axios.get(`${API}/admin/agreements/${agreementId}/pdf`, {
        headers, responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      triggerBlobDownload(blob, `${label || 'agreement'}.pdf`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download PDF');
    }
  };

  const downloadFiltered = async () => {
    if (!items.length) {
      toast.message('Nothing to export with the current filters.');
      return;
    }
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/agreements/export.zip`, {
        headers,
        params: {
          kind,
          region: region || undefined,
          q: search || undefined,
        },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/zip' });
      const scope = region ? `${kind}-${region}` : `${kind}-all-regions`;
      triggerBlobDownload(blob, `allure-agreements-${scope}.zip`);
      toast.success('ZIP downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to export ZIP');
    } finally {
      setExporting(false);
    }
  };

  const filteredLabel = useMemo(() => {
    if (!items.length) return 'No agreements yet';
    if (items.length === 1) return '1 agreement';
    return `${items.length} agreements`;
  }, [items.length]);

  return (
    <div data-testid="admin-agreements-panel" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <h2 style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 22, fontWeight: 700, color: ADMIN_PALETTE.ink }}>
            Legal Agreements
          </h2>
          <span style={{ fontSize: 12, fontWeight: 600, color: ADMIN_PALETTE.inkMuted }}>
            Version history · Signed copies · DAC7 ready
          </span>
        </div>
        <button
          type="button"
          onClick={downloadFiltered}
          disabled={exporting || !items.length}
          data-testid="admin-agreements-download-all"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            padding: '9px 14px', borderRadius: 12,
            border: `1px solid ${items.length ? ADMIN_PALETTE.ink : ADMIN_PALETTE.border}`,
            background: items.length ? ADMIN_PALETTE.ink : ADMIN_PALETTE.surface,
            color: items.length ? '#FFFFFF' : ADMIN_PALETTE.inkMuted,
            fontWeight: 700, fontSize: 13,
            cursor: exporting || !items.length ? 'not-allowed' : 'pointer',
            opacity: exporting ? 0.7 : 1,
          }}
        >
          <Download size={14} />
          {exporting ? 'Preparing ZIP…' : 'Download filtered (ZIP)'}
        </button>
      </div>

      {/* Folder tabs */}
      <div style={{ display: 'flex', gap: 10 }}>
        <Tab
          active={kind === 'venue'}
          onClick={() => { setKind('venue'); setRegion(''); setOnlyPending(false); }}
          label="Venues"
          count={counts.venue}
          testId="admin-agreements-tab-venue"
        />
        <Tab
          active={kind === 'host'}
          onClick={() => { setKind('host'); setRegion(''); setOnlyPending(false); }}
          label="Hosts"
          count={counts.host}
          testId="admin-agreements-tab-host"
        />
      </div>

      {/* Version banner */}
      <VersionBanner
        stats={stats}
        versions={versions}
        onOpenRollout={() => setRolloutOpen(true)}
        onOpenHistory={() => setHistoryOpen(true)}
      />

      {/* Filters row */}
      <div
        style={{
          display: 'flex', gap: 10, flexWrap: 'wrap',
          background: ADMIN_PALETTE.surface, padding: 12, borderRadius: 14,
          border: `1px solid ${ADMIN_PALETTE.border}`,
        }}
      >
        <div style={{ flex: '1 1 240px', display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 10, background: ADMIN_PALETTE.surfaceMuted, border: `1px solid ${ADMIN_PALETTE.border}` }}>
          <Search size={14} color={ADMIN_PALETTE.inkMuted} />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={kind === 'venue' ? 'Search email, venue name, region, city…' : 'Search email, listing, location…'}
            data-testid="admin-agreements-search"
            style={{
              flex: 1, border: 'none', outline: 'none', background: 'transparent',
              fontSize: 13, color: ADMIN_PALETTE.ink, fontFamily: 'inherit',
            }}
          />
        </div>
        <div style={{ position: 'relative', minWidth: 200 }}>
          <select
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            data-testid="admin-agreements-region-filter"
            style={{
              appearance: 'none', width: '100%', padding: '9px 30px 9px 12px',
              borderRadius: 10, border: `1px solid ${ADMIN_PALETTE.border}`,
              background: ADMIN_PALETTE.surfaceMuted, fontSize: 13,
              color: ADMIN_PALETTE.ink, fontFamily: 'inherit', cursor: 'pointer',
            }}
          >
            <option value="">All regions</option>
            {regions.map((r) => (<option key={r} value={r}>{r}</option>))}
          </select>
          <ChevronDown size={14} color={ADMIN_PALETTE.inkMuted} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
        </div>
        <button
          type="button"
          onClick={() => setOnlyPending((v) => !v)}
          data-testid="admin-agreements-toggle-pending"
          style={{
            padding: '9px 12px', borderRadius: 10,
            border: `1px solid ${onlyPending ? 'rgba(233, 76, 87, 0.55)' : ADMIN_PALETTE.border}`,
            background: onlyPending ? 'rgba(233, 76, 87, 0.12)' : ADMIN_PALETTE.surfaceMuted,
            color: onlyPending ? 'var(--status-danger-text, #B00020)' : ADMIN_PALETTE.ink,
            fontWeight: 700, fontSize: 12.5, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}
        >
          <AlertTriangle size={12} />
          Only pending re-sign
        </button>
      </div>

      <p data-testid="admin-agreements-count" style={{ margin: '4px 4px 0', fontSize: 12, fontWeight: 600, color: ADMIN_PALETTE.inkMuted, letterSpacing: '0.04em' }}>
        {loading ? 'Loading…' : filteredLabel}
      </p>

      {/* List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {!loading && items.length === 0 ? (
          <div
            data-testid="admin-agreements-empty"
            style={{
              padding: 28, borderRadius: 16, background: ADMIN_PALETTE.surface,
              border: `1px dashed ${ADMIN_PALETTE.border}`, textAlign: 'center',
              color: ADMIN_PALETTE.inkMuted, fontSize: 13,
            }}
          >
            <FileText size={20} style={{ marginBottom: 8, opacity: 0.6 }} />
            <p style={{ margin: 0 }}>
              No signed {kind === 'venue' ? 'venue' : 'host'} agreements yet.
            </p>
          </div>
        ) : null}
        {items.map((agreement) => {
          const summary = agreement.reaccept_summary || null;
          const signedCurrent = summary?.signed_current;
          const hardGated = summary?.is_hard_gated;
          return (
            <div
              key={agreement.id}
              data-testid={`admin-agreement-row-${agreement.id}`}
              style={{
                display: 'flex', gap: 12, padding: 14, borderRadius: 14,
                background: ADMIN_PALETTE.surface,
                border: `1px solid ${hardGated && agreement.is_latest_for_user ? 'rgba(233, 76, 87, 0.4)' : ADMIN_PALETTE.border}`,
                alignItems: 'center', flexWrap: 'wrap',
              }}
            >
              <div style={{ flex: '1 1 240px', minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: ADMIN_PALETTE.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {agreement.entity_name || agreement.user_email || 'Agreement'}
                </p>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: ADMIN_PALETTE.inkMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {agreement.user_full_name ? `${agreement.user_full_name} · ` : ''}{agreement.user_email}
                </p>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                {agreement.region ? (
                  <span style={{
                    padding: '4px 10px', borderRadius: 999,
                    background: STATUS_PALETTE.info.soft, color: STATUS_PALETTE.info.text,
                    fontSize: 11, fontWeight: 700,
                  }}>
                    {agreement.region}
                  </span>
                ) : null}
                {agreement.is_latest_for_user && summary ? (
                  <span
                    data-testid={`admin-agreement-row-${agreement.id}-status`}
                    style={{
                      padding: '4px 10px', borderRadius: 999,
                      background: signedCurrent
                        ? 'rgba(31, 168, 108, 0.16)'
                        : (hardGated ? 'rgba(233, 76, 87, 0.16)' : 'rgba(212, 149, 30, 0.16)'),
                      color: signedCurrent
                        ? 'var(--status-success-text, #1F7D50)'
                        : (hardGated ? 'var(--status-danger-text, #B00020)' : 'var(--status-warning-text, #A56514)'),
                      fontSize: 11, fontWeight: 800, letterSpacing: '0.03em',
                    }}
                  >
                    {signedCurrent
                      ? `Signed ${summary.current_version} ✓`
                      : (hardGated
                          ? `BLOCKED — needs ${summary.current_version}`
                          : `Pending ${summary.current_version}`)}
                  </span>
                ) : null}
                <span style={{ fontSize: 11.5, color: ADMIN_PALETTE.inkMuted, fontWeight: 600 }}>
                  {agreement.version}
                </span>
                <span style={{ fontSize: 11.5, color: ADMIN_PALETTE.inkMuted }}>
                  {formatDate(agreement.accepted_at)}
                </span>
              </div>
              <button
                type="button"
                onClick={() => downloadOne(agreement.id, `${agreement.kind}-${(agreement.entity_name || agreement.user_email || agreement.id).slice(0, 40)}`)}
                data-testid={`admin-agreement-download-${agreement.id}`}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '7px 11px', borderRadius: 10,
                  border: `1px solid ${ADMIN_PALETTE.border}`,
                  background: ADMIN_PALETTE.surfaceMuted, color: ADMIN_PALETTE.ink,
                  cursor: 'pointer', fontSize: 12, fontWeight: 700,
                }}
              >
                <Download size={13} />
                PDF
              </button>
            </div>
          );
        })}
      </div>

      {rolloutOpen ? (
        <AgreementRolloutModal
          kind={kind}
          currentVersion={stats?.current_version || ''}
          headers={headers}
          onClose={() => setRolloutOpen(false)}
          onRolledOut={() => { fetchVersions(); fetchList(); }}
        />
      ) : null}

      <VersionHistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        kind={kind}
        versions={versions}
        headers={headers}
        onLoadBody={loadVersionBody}
      />
    </div>
  );
}
